import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  type Airport,
  getAirportByIcao,
  getAirportsByIcaoCodes,
  searchAirports,
  searchAirportsDirectory,
  ensureAirportDb,
} from '../airport-db'
import { useOrpcTanstackQueryUtils } from '../orpc'

export type { Airport }

export function useAirportByIcao(icao: string | undefined | null): Airport | null {
  const [airport, setAirport] = useState<Airport | null>(null)

  useEffect(() => {
    if (!icao) {
      setAirport(null)
      return
    }
    ensureAirportDb()
      .then(() => setAirport(getAirportByIcao(icao)))
      .catch(() => setAirport(null))
  }, [icao])

  return airport
}

// Like useAirportByIcao but reports whether the local DB lookup has settled,
// so callers can gate rendering on it (null airport ≠ still loading).
export function useAirportByIcaoStatus(icao: string | undefined | null): {
  airport: Airport | null
  isLoading: boolean
} {
  const [airport, setAirport] = useState<Airport | null>(null)
  const [isLoading, setIsLoading] = useState(!!icao)

  useEffect(() => {
    if (!icao) {
      setAirport(null)
      setIsLoading(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    ensureAirportDb()
      .then(() => {
        if (!cancelled) setAirport(getAirportByIcao(icao))
      })
      .catch(() => {
        if (!cancelled) setAirport(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [icao])

  return { airport, isLoading }
}

export function useAirportsByIcaos(codes: string[]): Airport[] {
  const [airports, setAirports] = useState<Airport[]>([])
  // Key on content, not array identity — callers often build the array inline.
  const key = codes.join(',')

  useEffect(() => {
    if (!key) {
      setAirports([])
      return
    }
    ensureAirportDb()
      .then(() => setAirports(getAirportsByIcaoCodes(key.split(','))))
      .catch(() => setAirports([]))
  }, [key])

  return airports
}

// Leon-sourced airport intel (overview/runway/fuel/customs), cached server-side
// for 7 days. Returns null when the airport is unknown to Leon.
export function useAirportIntel(icao?: string | null) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.airport.getIntel.queryOptions({ input: { icao: icao ?? '' } }),
    enabled: !!icao && icao.length >= 3,
    staleTime: 60 * 60 * 1000,
  })
}

// Directory-flavored search (ICAO-first ranking) — see searchAirportsDirectory.
export function useAirportDirectorySearch(
  query: string,
  limit = 200
): { results: Airport[]; isLoading: boolean } {
  const [results, setResults] = useState<Airport[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!query) {
      setResults([])
      setIsLoading(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    ensureAirportDb()
      .then(() => {
        if (cancelled) return
        setResults(searchAirportsDirectory(query, limit))
      })
      .catch(() => {
        if (!cancelled) setResults([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [query, limit])

  return { results, isLoading }
}

export function useAirportSearch(
  query: string,
  limit = 50
): { results: Airport[]; isLoading: boolean } {
  const [results, setResults] = useState<Airport[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([])
      setIsLoading(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    ensureAirportDb()
      .then(() => {
        if (cancelled) return
        setResults(searchAirports(query, limit))
      })
      .catch(() => {
        if (!cancelled) setResults([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [query, limit])

  return { results, isLoading }
}
